import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Avatar,
  Collapse,
  Tooltip,
  Divider,
  alpha,
} from '@mui/material';
import {
  Dashboard,
  People,
  CalendarToday,
  RoomService,
  Group,
  Inventory,
  AccountBalance,
  Campaign,
  Analytics,
  Settings,
  ExpandMore,
  CardMembership,
  SupervisorAccount,
  ContactSupport,
  DevicesOther,
  FiberManualRecord,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { selectCurrentSalonId, selectTenants } from '../../store/slices/tenantSlice';
import { isPlatformOnlySuperAdmin } from '../../utils/platformAdmin';

const DRAWER_WIDTH = 280;
const MINI_DRAWER_WIDTH = 76;
const ACCENT = '#D4969B';
const ACCENT_SOFT = '#F5D5D8';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path?: string;
  children?: MenuItem[];
  roles?: string[];
}

interface MenuSection {
  id: string;
  label: string;
  items: MenuItem[];
}

const sections: MenuSection[] = [
  {
    id: 'workspace',
    label: 'Workspace',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { id: 'appointments', label: 'Appointments', icon: <CalendarToday />, path: '/appointments' },
      { id: 'customers', label: 'Customers', icon: <People />, path: '/customers' },
      {
        id: 'inquiries',
        label: 'Inquiries',
        icon: <ContactSupport />,
        path: '/inquiries',
        roles: ['super_admin', 'salon_owner', 'manager'],
      },
      {
        id: 'whatsapp-inbox',
        label: 'WhatsApp Inbox',
        icon: <WhatsAppIcon />,
        path: '/whatsapp-inbox',
      },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    items: [
      {
        id: 'services',
        label: 'Services & Packages',
        icon: <RoomService />,
        children: [
          { id: 'services-list', label: 'Services', icon: <RoomService />, path: '/services' },
          { id: 'packages', label: 'Packages', icon: <RoomService />, path: '/packages' },
        ],
      },
      {
        id: 'inventory',
        label: 'Inventory',
        icon: <Inventory />,
        path: '/inventory',
        roles: ['super_admin', 'salon_owner', 'manager'],
      },
    ],
  },
  {
    id: 'business',
    label: 'Business',
    items: [
      {
        id: 'staff',
        label: 'Staff Management',
        icon: <Group />,
        path: '/staff',
        roles: ['super_admin', 'salon_owner', 'manager'],
      },
      {
        id: 'finance',
        label: 'Finance',
        icon: <AccountBalance />,
        children: [
          { id: 'payments', label: 'Payments', icon: <AccountBalance />, path: '/finance/payments' },
          { id: 'expenses', label: 'Expenses', icon: <AccountBalance />, path: '/finance/expenses' },
          { id: 'reports', label: 'Reports', icon: <Analytics />, path: '/finance/reports' },
        ],
        roles: ['super_admin', 'salon_owner', 'manager'],
      },
      {
        id: 'marketing',
        label: 'Marketing',
        icon: <Campaign />,
        path: '/marketing',
        roles: ['super_admin', 'salon_owner', 'manager'],
      },
      {
        id: 'membership',
        label: 'Membership',
        icon: <CardMembership />,
        path: '/membership',
        roles: ['super_admin', 'salon_owner', 'manager'],
      },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: <Analytics />,
        path: '/analytics',
        roles: ['super_admin', 'salon_owner', 'manager'],
      },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      {
        id: 'kiosks',
        label: 'Kiosks',
        icon: <DevicesOther />,
        path: '/kiosks',
        roles: ['super_admin', 'salon_owner', 'manager'],
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: <Settings />,
        path: '/settings',
        roles: ['super_admin', 'salon_owner'],
      },
      {
        id: 'super-admin',
        label: 'Super Admin',
        icon: <SupervisorAccount />,
        path: '/super-admin',
        roles: ['super_admin'],
      },
    ],
  },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const currentSalonId = useAppSelector(selectCurrentSalonId);
  const tenants = useAppSelector(selectTenants);
  const salonDisplayName =
    tenants.find((t: { salonId: string; name: string }) => t.salonId === currentSalonId)?.name ||
    'Salon CRM';
  const [openItems, setOpenItems] = React.useState<string[]>([]);

  // Auto-open the group that contains the current route so the nav reflects
  // where the user actually is when they land directly on a sub-page.
  React.useEffect(() => {
    const containingGroups: string[] = [];
    for (const section of sections) {
      for (const item of section.items) {
        if (item.children?.some((c) => c.path && location.pathname.startsWith(c.path))) {
          containingGroups.push(item.id);
        }
      }
    }
    if (containingGroups.length) {
      setOpenItems((prev) => Array.from(new Set([...prev, ...containingGroups])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleItemClick = (item: MenuItem) => {
    if (item.children) {
      const isOpen = openItems.includes(item.id);
      setOpenItems(isOpen ? openItems.filter((id) => id !== item.id) : [...openItems, item.id]);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const isItemActive = (path?: string) => !!path && location.pathname === path;

  const isGroupActive = (item: MenuItem) =>
    !!item.children?.some((c) => c.path && location.pathname.startsWith(c.path));

  const hasPermission = (item: MenuItem) => {
    if (!user) return false;
    if (isPlatformOnlySuperAdmin(user)) {
      return item.id === 'super-admin' || item.id === 'settings';
    }
    if (!item.roles) return true;
    const normalizedUserRole = user.role.toLowerCase();
    const normalizedRequiredRoles = item.roles.map((r) => r.toLowerCase());
    return normalizedRequiredRoles.includes(normalizedUserRole);
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
    if (!hasPermission(item)) return null;

    const isOpen = openItems.includes(item.id);
    const isActive = isItemActive(item.path);
    const isParentOfActive = isGroupActive(item);
    const highlight = isActive || (item.children && isParentOfActive);

    // Collapsed (mini) drawer — show only icons with tooltip; clicking a
    // parent jumps straight to its first child for fewer surprises.
    if (!sidebarOpen) {
      const button = (
        <ListItemButton
          onClick={() => {
            if (item.children) {
              const first = item.children.find((c) => c.path);
              if (first?.path) navigate(first.path);
            } else if (item.path) {
              navigate(item.path);
            }
          }}
          selected={!!highlight}
          sx={{
            mx: 1,
            my: 0.5,
            px: 0,
            py: 1.25,
            borderRadius: 2,
            justifyContent: 'center',
            position: 'relative',
            '&::before': highlight
              ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 8,
                  bottom: 8,
                  width: 3,
                  borderRadius: 2,
                  backgroundColor: ACCENT,
                  boxShadow: `0 0 12px ${alpha(ACCENT, 0.6)}`,
                }
              : undefined,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: 'center',
              color: highlight ? ACCENT_SOFT : 'rgba(247, 248, 250, 0.72)',
            }}
          >
            {item.icon}
          </ListItemIcon>
        </ListItemButton>
      );

      return (
        <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
          <Tooltip title={item.label} placement="right" arrow>
            {button}
          </Tooltip>
        </ListItem>
      );
    }

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            selected={isActive || (!!item.children && isParentOfActive && !isOpen)}
            sx={{
              mx: 1.25,
              my: 0.25,
              pl: 1.25 + level * 1.5,
              pr: 1.25,
              py: 1,
              borderRadius: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'background-color 160ms ease, transform 160ms ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& .nav-icon': { color: '#FFFFFF', transform: 'translateX(2px)' },
              },
              '&.Mui-selected': {
                backgroundColor: 'transparent',
                background: `linear-gradient(90deg, ${alpha(ACCENT, 0.22)} 0%, ${alpha(ACCENT, 0.04)} 100%)`,
                '&:hover': {
                  background: `linear-gradient(90deg, ${alpha(ACCENT, 0.28)} 0%, ${alpha(ACCENT, 0.06)} 100%)`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 8,
                  bottom: 8,
                  width: 3,
                  borderRadius: 2,
                  backgroundColor: ACCENT,
                  boxShadow: `0 0 14px ${alpha(ACCENT, 0.55)}`,
                },
              },
            }}
          >
            <ListItemIcon
              className="nav-icon"
              sx={{
                minWidth: 36,
                color: highlight ? ACCENT_SOFT : 'rgba(247, 248, 250, 0.62)',
                transition: 'color 160ms ease, transform 160ms ease',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: highlight ? 600 : 500,
                color: highlight ? '#FFFFFF' : 'rgba(247, 248, 250, 0.82)',
                letterSpacing: 0.1,
              }}
            />
            {item.children && (
              <ExpandMore
                fontSize="small"
                sx={{
                  color: 'rgba(247, 248, 250, 0.5)',
                  transition: 'transform 200ms ease',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
        {item.children && (
          <Collapse in={isOpen} timeout={200} unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 0.5 }}>
              {item.children.map((child) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const renderSection = (section: MenuSection) => {
    const visibleItems = section.items.filter(hasPermission);
    if (visibleItems.length === 0) return null;

    return (
      <Box key={section.id} sx={{ mb: 1.25 }}>
        {sidebarOpen ? (
          <Typography
            sx={{
              px: 3,
              pt: 1.5,
              pb: 0.75,
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(247, 248, 250, 0.38)',
            }}
          >
            {section.label}
          </Typography>
        ) : (
          <Divider
            sx={{
              mx: 2,
              my: 1,
              borderColor: 'rgba(255, 255, 255, 0.06)',
            }}
          />
        )}
        {visibleItems.map((item) => renderMenuItem(item))}
      </Box>
    );
  };

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      open={true}
      sx={{
        width: sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          // Layered gradient: deep wine top-left → near-black bottom-right
          // with two faint radial spotlights for depth.
          background: '#0F1218',
          backgroundImage: `
            radial-gradient(circle at 0% 0%, ${alpha(ACCENT, 0.16)} 0%, transparent 45%),
            radial-gradient(circle at 100% 100%, rgba(184, 134, 11, 0.08) 0%, transparent 50%),
            linear-gradient(180deg, #15101A 0%, #0F1218 60%, #0B0E13 100%)
          `,
          borderRight: 'none',
          boxShadow: '4px 0 24px rgba(0, 0, 0, 0.25)',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Brand block */}
      <Box
        sx={{
          p: sidebarOpen ? 2.25 : 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarOpen ? 'flex-start' : 'center',
          gap: 1.5,
          minHeight: 72,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 16,
            right: 16,
            bottom: 0,
            height: 1,
            background:
              'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.12) 50%, transparent 100%)',
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontWeight: 700,
              fontSize: '1rem',
              color: '#0F1218',
              background: `linear-gradient(135deg, ${ACCENT_SOFT} 0%, ${ACCENT} 100%)`,
              boxShadow: `0 6px 16px ${alpha(ACCENT, 0.35)}`,
            }}
          >
            {(salonDisplayName?.[0] || 'S').toUpperCase()}
          </Avatar>
          {/* Online status dot */}
          <FiberManualRecord
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              fontSize: 12,
              color: '#22C55E',
              filter: 'drop-shadow(0 0 4px rgba(34, 197, 94, 0.6))',
              backgroundColor: '#0F1218',
              borderRadius: '50%',
            }}
          />
        </Box>
        {sidebarOpen && (
          <Box sx={{ overflow: 'hidden', flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: '#FFFFFF',
                fontSize: '0.9375rem',
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {salonDisplayName}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(247, 248, 250, 0.5)',
                fontSize: '0.6875rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {user && isPlatformOnlySuperAdmin(user) ? 'Platform Console' : 'Workspace'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Menu */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1, '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 3 } }}>
        {sections.map(renderSection)}
      </Box>

      {/* User card */}
      {user && (
        <Box
          sx={{
            p: sidebarOpen ? 1.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarOpen ? 'flex-start' : 'center',
            gap: 1.25,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 16,
              right: 16,
              top: 0,
              height: 1,
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.12) 50%, transparent 100%)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontWeight: 700,
              fontSize: '0.8125rem',
              color: '#FFFFFF',
              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.55)} 0%, ${alpha(ACCENT, 0.25)} 100%)`,
              border: `1px solid ${alpha(ACCENT, 0.4)}`,
            }}
          >
            {user.firstName[0]}{user.lastName[0]}
          </Avatar>
          {sidebarOpen && (
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  lineHeight: 1.25,
                }}
                noWrap
              >
                {user.firstName} {user.lastName}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(247, 248, 250, 0.55)',
                  fontSize: '0.6875rem',
                  fontWeight: 500,
                  letterSpacing: 0.2,
                }}
                noWrap
              >
                {user.role
                  .replace(/_/g, ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
